import type { AdminCompanyDetailDto } from '@@types/api.types';
import { EditSectionControls } from '@components/shared/edit-section-controls.component';
import { SectionCard } from '@components/shared/section-card.component';
import { Badge } from '@components/ui/badge';
import { useAppForm } from '@components/ui/form/form';
import { errorToast, successToast } from '@hooks/toast.hook';
import { useAdminUpdateOrganizationTags } from '@queries/admin-organizations.query';
import { adminOrganizationTagsSchema } from '@schemas/admin-organization.schema';
import { useStore } from '@tanstack/react-form';
import { hasArrayChanges } from '@utils/array.util';
import { createFormInvalidHandler } from '@utils/form.util';
import { getImplicitTagSourceLabel, isAllTagName } from '@utils/tag.util';
import { XIcon } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { AdminOrganizationTagsFormData, adminOrganizationTagsFormOptions } from './admin-organizations-form-options';

type AdminOrganizationTagsTileProps = {
  availableTagNames: string[];
  isDeleted: boolean;
  organization: AdminCompanyDetailDto;
  organizationId: string;
};

const normalizeCustomTagNames = (tagNames: string[]): string[] => {
  const normalizedTagNames: string[] = [];
  const seen = new Set<string>();

  for (const tagName of tagNames) {
    const trimmedTagName = tagName.trim();
    if (!trimmedTagName) continue;

    const normalizedTagNameKey = trimmedTagName.toLowerCase();
    if (seen.has(normalizedTagNameKey)) continue;

    seen.add(normalizedTagNameKey);
    normalizedTagNames.push(trimmedTagName);
  }

  return normalizedTagNames;
};

export const AdminOrganizationTagsTile: React.FC<AdminOrganizationTagsTileProps> = (props) => {
  const { availableTagNames, isDeleted, organization, organizationId } = props;

  const [createdTagNames, setCreatedTagNames] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);

  const initialTagNames = useMemo(
    () => organization.companyTags?.map((tag) => tag.name) ?? [],
    [organization.companyTags],
  );
  const defaultTagValues = useMemo<AdminOrganizationTagsFormData>(
    () => ({
      selectedTagNames: initialTagNames,
    }),
    [initialTagNames],
  );

  const { isPending: isUpdatingTags, mutateAsync: updateOrganizationTags } = useAdminUpdateOrganizationTags();

  const form = useAppForm({
    ...adminOrganizationTagsFormOptions,
    defaultValues: defaultTagValues,
    onSubmit: async ({ value }) => {
      const normalizedTagNames = normalizeCustomTagNames(value.selectedTagNames);
      const hasChanges = hasArrayChanges({
        next: normalizedTagNames,
        previous: initialTagNames,
      });

      if (!hasChanges) {
        form.reset(defaultTagValues);
        setCreatedTagNames([]);
        setIsEditing(false);
        return;
      }

      try {
        await updateOrganizationTags({
          data: {
            tags: normalizedTagNames,
          },
          id: organizationId,
        });
        form.reset({ selectedTagNames: normalizedTagNames });
        successToast({ description: 'Tags updated successfully.' });
        setCreatedTagNames([]);
        setIsEditing(false);
      } catch (error) {
        if (error instanceof Error) {
          errorToast({ description: error.message });
          return;
        }

        errorToast({ description: 'Failed to update tags.' });
      }
    },
    onSubmitInvalid: createFormInvalidHandler(),
    validators: {
      onSubmit: adminOrganizationTagsSchema,
    },
  });

  const selectedTagNames = useStore(form.store, (state) => state.values.selectedTagNames ?? []);

  const allSelectableCustomTagOptions = useMemo(() => {
    const allTagNames = normalizeCustomTagNames([...availableTagNames, ...createdTagNames]);

    return allTagNames.map((tagName) => ({
      description: 'Custom',
      label: tagName,
      value: tagName,
    }));
  }, [availableTagNames, createdTagNames]);

  const handleCreateTagOption = async (tagName: string): Promise<void> => {
    const normalizedTagName = normalizeCustomTagNames([tagName])[0];
    if (!normalizedTagName) return;

    form.setFieldValue('selectedTagNames', normalizeCustomTagNames([...selectedTagNames, normalizedTagName]));
    setCreatedTagNames((currentTagNames) => normalizeCustomTagNames([...currentTagNames, normalizedTagName]));
  };

  const handleCancelEdit = (): void => {
    form.reset(defaultTagValues);
    setCreatedTagNames([]);
    setIsEditing(false);
  };

  return (
    <SectionCard>
      <EditSectionControls
        cancelAriaLabel="Cancel Tags"
        editAriaLabel="Edit Tags"
        editDisabledTooltip={isDeleted ? 'Cannot edit deleted organization' : undefined}
        isCancelDisabled={isUpdatingTags}
        isEditDisabled={isDeleted}
        isEditing={isEditing}
        isSaveDisabled={isUpdatingTags}
        onCancel={handleCancelEdit}
        onEdit={() => {
          form.reset(defaultTagValues);
          setCreatedTagNames([]);
          setIsEditing(true);
        }}
        onSave={() => form.handleSubmit()}
        saveAriaLabel="Save Tags"
      />

      <div className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-gray-900">Access Controls</h3>

        <div className="flex flex-wrap gap-1.5">
          {organization.implicitTags?.map((tag) => (
            <Badge
              key={`implicit-${tag.source}-${tag.name}`}
              size="xs"
              variant="secondary"
            >
              {isAllTagName(tag.name) ? tag.name : `${getImplicitTagSourceLabel(tag.source)}: ${tag.name}`}
            </Badge>
          ))}

          {!isEditing &&
            organization.companyTags?.map((tag) => (
              <Badge
                key={tag.id}
                size="xs"
                variant="innovator"
              >
                {tag.name}
              </Badge>
            ))}

          {!isEditing &&
            (!organization.implicitTags || organization.implicitTags.length === 0) &&
            (!organization.companyTags || organization.companyTags.length === 0) && (
              <span className="text-sm text-gray-500">No access control tags assigned.</span>
            )}
        </div>

        {isEditing && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              form.handleSubmit();
            }}
          >
            <form.AppForm>
              <form.AppField name="selectedTagNames">
                {(field) => (
                  <field.SelectMultipleField
                    disabled={isUpdatingTags}
                    label="Custom Tags"
                    onCreateOption={handleCreateTagOption}
                    options={allSelectableCustomTagOptions}
                    placeholder="Select or type tags..."
                    renderBadge={(params) => {
                      const { label, remove } = params;
                      return (
                        <Badge
                          className="group/tag border-transparent"
                          size="xs"
                          variant="innovator"
                        >
                          {label}
                          <button
                            className="ml-1 rounded-full hover:bg-gray-200/50"
                            disabled={isUpdatingTags}
                            onClick={(event) => {
                              event.stopPropagation();
                              remove();
                            }}
                            type="button"
                          >
                            <XIcon className="size-3" />
                          </button>
                        </Badge>
                      );
                    }}
                  />
                )}
              </form.AppField>
            </form.AppForm>
          </form>
        )}
      </div>
    </SectionCard>
  );
};
